import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect } from 'react';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import { Box, MenuItem, TextField, Typography, useTheme } from '@mui/material';
import AdminItemContainer from 'pages/adminPages/components/adminItemContainer';
import ColorIndicator from 'pages/adminPages/components/colorIndicator';
import map from 'lodash-es/map';
import { Role } from 'app/store/lib/authStore';
import IconTrash from 'shared/icons/trash';
import WarningModal from 'shared/lib/components/modals/warningModal';
import { AdminUserModel } from 'entities/adminUser/model/adminUserModel';
import filter from 'lodash-es/filter';
import ListSkeleton from 'shared/lib/components/plugs/listSkeleton';
import SearchTextField from 'shared/lib/components/controls/textFields/searchTextField';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { useBooleanScreens } from 'shared/hooks/useBooleanScreens';
import ActionInlineButton from 'shared/lib/components/controls/buttons/actionInlineButton';
import IconCirclePlus from 'shared/icons/iconCirclePlus';
import Modal from 'shared/lib/components/modals/modal';
import CreateUserForm from 'pages/adminPages/createUserForm';
import { useTranslation } from 'react-i18next';
import { adminUsersApi } from 'entities/adminUser/api/adminUsersApi';
import BlockHeader from 'pages/components/blockHeader';
import EmptyList from 'shared/lib/components/plugs/emptyList';
export type AdminInnerProps = {
  search: string;
  setSearch: (value: string) => void;
};

export default observer(function AdminUsersInner({
  search,
  setSearch,
}: AdminInnerProps) {
  const { t } = useTranslation();
  const { mobxStore } = useContext(MobxContext);

  const theme = useTheme();
  const {
    spacings: { sp },
  } = useContext(UiContext);

  useEffect(() => {
    mobxStore.adminUsersStore.fetch();
  }, [mobxStore.adminUsersStore]);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [roleChangeData, setRoleChangeData] = useState<{
    user: AdminUserModel;
    role: Role;
  } | null>(null);

  const isUsers = !!mobxStore.adminUsersStore.users;

  const filtered = useMemo(
    () =>
      filter(
        mobxStore.adminUsersStore.users,
        (user) =>
          user.name.toLowerCase().includes(search.toLowerCase()) ||
          user.email.toLowerCase().includes(search.toLowerCase())
      ),
    [mobxStore.adminUsersStore.users, search]
  );

  const { isLessLaptop, isLessTablet, isLessDesktopSmall } =
    useBooleanScreens();

  return (
    <>
      <BlockHeader text="Пользователи"></BlockHeader>

      {isUsers && filtered.length ? (
        <Box
          sx={{
            position: 'relative',
            display: isLessTablet ? 'flex' : 'grid',
            flexDirection: 'column',
            gridTemplateColumns: 'min-content 4fr 3fr min-content',
            rowGap: 2,
            gridAutoFlow: 'row',
            gridAutoRows: '1fr',
          }}
        >
          {map(filtered, (user) => {
            const roleColor = theme.palette.custom.roles[user.role];

            return (
              <Box
                sx={{
                  'display': isLessTablet ? 'flex' : 'contents',
                  'flexDirection': 'column',
                  'alignItems': 'center',
                  'width': 1,
                  'borderRadius': '10px',
                  'overflow': 'hidden',
                  '& > *': { width: 1 },
                }}
                key={user.id}
              >
                <ColorIndicator
                  isHorizontal={isLessTablet}
                  color={roleColor}
                ></ColorIndicator>
                <AdminItemContainer
                  sx={{
                    px: isLessLaptop ? 4 : 17,
                    justifyContent: isLessTablet ? 'center' : 'start',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isLessTablet ? 'center' : 'start',
                      gap: 1,
                    }}
                  >
                    <Typography variant="f18b">{user.name}</Typography>
                    <Typography variant="f14">{user.email}</Typography>
                  </Box>
                </AdminItemContainer>
                <AdminItemContainer sx={{ px: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexGrow: 1,
                      width: 1,
                      height: 1,
                      px: isLessDesktopSmall ? 4 : 17,
                      borderRight: isLessTablet ? 0 : 1,
                      borderLeft: isLessTablet ? 0 : 1,
                      borderColor: 'custom.borders',
                    }}
                  >
                    <TextField
                      select
                      onChange={(e) =>
                        setRoleChangeData({
                          role: e.target.value as Role,
                          user,
                        })
                      }
                      value={user.role}
                      SelectProps={{
                        MenuProps: {
                          sx: {
                            '& .MuiPaper-root': {
                              border: 1,
                              borderColor: 'custom.borders',
                            },
                          },
                        },
                      }}
                      sx={{
                        'width': 1,
                        'maxWidth': 330,
                        '& .MuiSelect-select': {
                          p: 3,
                          textAlign: 'center',
                          color: 'primary.contrastText',
                          borderRadius: '100 !important',
                          overflow: 'hidden',
                          bgcolor: roleColor,
                        },
                        '& .MuiSelect-icon': {
                          fill: theme.palette.primary.contrastText,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                      }}
                    >
                      <MenuItem value={Role.ADMIN}>Администратор</MenuItem>
                      <MenuItem value={Role.EDITOR}>Редактор</MenuItem>
                      <MenuItem value={Role.VIEWER}>Читатель</MenuItem>
                    </TextField>
                  </Box>
                </AdminItemContainer>
                <AdminItemContainer
                  sx={{
                    justifyContent: isLessTablet ? 'center' : 'start',
                    borderTopRightRadius: 10,
                    borderBottomRightRadius: 10,

                    ...(isLessTablet
                      ? {
                          position: 'absolute',
                          background: 'transparent',
                          svg: {
                            position: 'absolute',
                            right: theme.spacing(sp.s60),
                            top: theme.spacing(sp.s60),
                            transform: 'translate(0, 50%)',
                          },
                        }
                      : {}),
                  }}
                >
                  <IconTrash
                    sizeKey="24x24"
                    extColor={theme.palette.error.main}
                    onClick={() => setDeleteId(user.id)}
                  ></IconTrash>
                </AdminItemContainer>
              </Box>
            );
          })}

          <WarningModal
            isOpen={!!deleteId}
            onCancel={() => setDeleteId(null)}
            onContinue={async () => {
              if (deleteId) {
                try {
                  await mobxStore.adminUsersStore.delete(deleteId);
                  setDeleteId(null);
                } catch (e) {
                  console.error(e);
                  setDeleteId(null);
                }
              }
            }}
            mainText="Вы уверены, что хотите удалить пользователя?"
          ></WarningModal>

          <WarningModal
            isOpen={!!roleChangeData}
            continueText="Продолжить"
            onCancel={() => setRoleChangeData(null)}
            onContinue={async () => {
              if (roleChangeData) {
                try {
                  await roleChangeData.user.setRole(roleChangeData.role);
                  setRoleChangeData(null);
                } catch (e) {
                  console.error(e);
                  setRoleChangeData(null);
                  mobxStore.notificationsStore.errorMessage =
                    'Не удалось изменить роль';
                }
              }
            }}
            mainText="Вы уверены, что хотите поменять роль пользователю?"
          ></WarningModal>
        </Box>
      ) : mobxStore.adminUsersStore.users?.length ? (
        <EmptyList text="Совпадений нет"></EmptyList>
      ) : mobxStore.adminUsersStore.users ? (
        <EmptyList text="Ничего не найдено"></EmptyList>
      ) : (
        <ListSkeleton></ListSkeleton>
      )}
    </>
  );
});
